import { Button, Input, Text, VSpacer } from '@/components/DesignSystem';
import { AppConfig } from '@/constants/AppConfig';
import { AuthApi } from '@/utilities/api/AuthApi';
import { isValidPhoneNumber } from '@shared/utilities';
import { debounce } from 'lodash';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation } from 'react-router-dom';

interface LoginTelephoneInputProps {
  onConfirmSuccess: ({ lastFour, telephone }: { lastFour: string, telephone: string }) => void,
}

export const LoginTelephoneInput = ({
  onConfirmSuccess,
}: LoginTelephoneInputProps) => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const [telephone, setTelephone] = useState(state?.username ?? "");
  const [errorMessage, setErrorMessage] = (
    useState<{ title?: string, message: string } | undefined>(state?.errorMessage)
  );

  const { mutate: sendOtpCode, isLoading: isSendingOtp } = useMutation(
    () => AuthApi.sendOtpCode({ telephone: telephone.replace(/[() -]/g, "") }),
    {
      onError: () => setErrorMessage({
        message: t($ => $.auth.errors.notRegistered),
      }),
      onSuccess: (data) => {
        setErrorMessage(undefined);
        onConfirmSuccess({ telephone, lastFour: data.lastFour });
      },
      retry: false,
    },
  );

  const handleInputChange = useCallback(
    debounce((telephone: string) => {
      if (!telephone || isValidPhoneNumber(telephone.replace(/[() -]/g, ""), AppConfig.env.test)) {
        setErrorMessage(undefined);
      } else {
        setErrorMessage({ message: t($ => $.auth.errors.invalidPhoneOrEmail) });
      }
    }, 1000),
    [],
  );

  const handleOnContinue = async () => {
    if (isValidPhoneNumber(telephone.replace(/[() -]/g, ""), AppConfig.env.test)) {
      setErrorMessage(undefined);
      sendOtpCode();
    } else if (telephone.length) {
      setErrorMessage({ message: t($ => $.auth.errors.invalidPhoneOrEmail) });
    }
  };

  return (
    <>
      {!!errorMessage && (
        <>
          <Text category="body-medium" color="error">
            {errorMessage.message}
          </Text>
          <VSpacer size="6" />
        </>
      )}
      <Input
        error={!!errorMessage}
        label={t($ => $.auth.login.phonePlaceholder)}
        onChangeText={(username) => {
          setTelephone(username);
          handleInputChange(username);
        }}
        onEnterPressed={handleOnContinue}
        testID="login-form-username"
        value={telephone}
        variant="outlined"
        width="375px"
      />
      <VSpacer size="8" />
      <Button
        disabled={!isValidPhoneNumber(telephone.replace(/[() -]/g, ""), AppConfig.env.test)}
        loading={isSendingOtp}
        onClick={handleOnContinue}
        testID="login-form-button"
      >
        Continue
      </Button>
    </>
  );
};
